import type { FC, ReactNode } from 'react'

import { MessageSquare } from 'lucide-react'
import { COORDINATOR_COLOR, coordinator, teams } from '@/constants/agents'

const TeamsSection: FC = (): ReactNode => {
    const CoordinatorIcon = coordinator.icon

    return (
        <section id='agents' className='relative py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='mx-auto max-w-2xl text-center'>
                    <p className='font-mono text-xs uppercase tracking-[0.2em] text-primary'>
                        The agents
                    </p>
                    <h2 className='mt-4 font-heading text-3xl font-semibold tracking-tight text-balance sm:text-5xl'>
                        Meet your 21 specialists
                    </h2>
                    <p className='mt-5 text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                        Four teams of five, each agent focused on one job, plus the Coordinator that keeps them working together.
                    </p>
                </div>

                <article
                    className='relative mt-14 overflow-hidden rounded-3xl border p-6 sm:p-10'
                    style={{ borderColor: `${COORDINATOR_COLOR}55`, backgroundImage: `radial-gradient(ellipse at top left, ${COORDINATOR_COLOR}29, transparent 60%)` }}
                >
                    <div className='flex flex-col gap-6 md:flex-row md:items-center md:justify-between'>
                        <div className='flex items-start gap-5'>
                            <span
                                className='flex size-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-[0_0_32px_oklch(0.74_0.16_292/0.5)]'
                                style={{ backgroundImage: 'radial-gradient(circle at 35% 30%, #ddd0ff, #8b5cf6 55%, #4c1d95)' }}
                            >
                                <CoordinatorIcon className='size-6' aria-hidden='true' />
                            </span>

                            <div>
                                <p className='text-xs font-medium uppercase tracking-wider text-primary'>
                                    Agent 1 of 21
                                </p>
                                <h3 className='mt-1 font-heading text-2xl font-semibold'>
                                    The {coordinator.name}
                                </h3>
                                <p className='mt-2 max-w-2xl leading-relaxed text-muted-foreground'>
                                    {coordinator.role}
                                </p>
                            </div>
                        </div>

                        <p className='inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm font-medium md:self-center'>
                            <MessageSquare className='size-4 text-primary' aria-hidden='true' />
                            You talk to it. It talks to everyone else.
                        </p>
                    </div>
                </article>

                <div className='mt-6 grid gap-6 md:grid-cols-2'>
                    {teams.map((team) => {
                        const TeamIcon = team.icon

                        return (
                            <article
                                key={team.id}
                                className='rounded-3xl border border-white/10 bg-card/60 p-6 sm:p-8'
                                style={{ backgroundImage: `radial-gradient(ellipse at top right, ${team.color}14, transparent 55%)` }}
                            >
                                <div className='flex items-center gap-4'>
                                    <span
                                        className='flex size-12 shrink-0 items-center justify-center rounded-2xl border'
                                        style={{ borderColor: `${team.color}66`, backgroundColor: `${team.color}1a`, color: team.color }}
                                    >
                                        <TeamIcon className='size-6' aria-hidden='true' />
                                    </span>

                                    <div>
                                        <h3 className='font-heading text-xl font-semibold'>
                                            {team.name}
                                        </h3>
                                        <p className='text-sm' style={{ color: team.color }}>
                                            {team.tagline}
                                        </p>
                                    </div>
                                </div>

                                <p className='mt-4 text-sm leading-relaxed text-muted-foreground'>
                                    {team.summary}
                                </p>

                                <ul className='mt-6 divide-y divide-white/[0.06] border-t border-white/[0.06]'>
                                    {team.agents.map((agent) => {
                                        const AgentIcon = agent.icon

                                        return (
                                            <li key={agent.id} className='flex gap-3.5 py-3.5'>
                                                <span
                                                    className='mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg'
                                                    style={{ backgroundColor: `${team.color}14`, color: team.color }}
                                                >
                                                    <AgentIcon className='size-4' aria-hidden='true' />
                                                </span>

                                                <div>
                                                    <p className='text-sm font-semibold'>
                                                        {agent.name}
                                                    </p>
                                                    <p className='mt-0.5 text-sm leading-relaxed text-muted-foreground'>
                                                        {agent.role}
                                                    </p>
                                                </div>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </article>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

export default TeamsSection
